<template>
  <section
    class="sa-panel"
    aria-labelledby="sa-panel-heading"
  >
    <!-- Eyebrow: dark-on-light inverted band signals 'admin territory' without shouting -->
    <div class="sa-panel__eyebrow">
      <span class="sa-panel__eyebrow-chip">
        <span class="sa-panel__eyebrow-dot" aria-hidden="true"></span>
        <span class="sa-panel__eyebrow-label">Super-admin</span>
      </span>
      <span class="sa-panel__eyebrow-sep" aria-hidden="true">·</span>
      <h2 id="sa-panel-heading" class="sa-panel__title">Page controls</h2>
    </div>

    <!-- Row 1: Page template -->
    <div class="sa-row">
      <div class="sa-row__lede">
        <label for="sa-type-select" class="sa-row__label">Page template</label>
        <p class="sa-row__help">
          Controls which front-end layout renders at
          <a class="sa-link" :href="publicUrl" target="_blank" rel="noopener">
            /campaigns/{{ campaign.id }}<span class="sa-link__arrow" aria-hidden="true">↗</span>
          </a>
        </p>
      </div>
      <div class="sa-row__controls">
        <div class="sa-select">
          <select
            id="sa-type-select"
            v-model="selectedTypeId"
            :disabled="saving"
            class="sa-select__input"
          >
            <option :value="null">Default (no template)</option>
            <option
              v-for="type in visibleTypes"
              :key="type.id"
              :value="type.id"
            >{{ formatTypeLabel(type) }}</option>
          </select>
          <span class="sa-select__chevron" aria-hidden="true">▾</span>
        </div>
        <button
          type="button"
          class="sa-btn sa-btn--primary"
          :disabled="saving || !isDirty"
          @click="save"
        >
          <span v-if="saving" class="sa-btn__label">
            <span class="sa-spinner" aria-hidden="true"></span>
            Saving
          </span>
          <span v-else-if="isDirty" class="sa-btn__label">Save template</span>
          <span v-else class="sa-btn__label">Saved</span>
        </button>
      </div>
    </div>

    <div v-if="statusMessage" class="sa-status" :class="`sa-status--${statusVariant}`" role="status">
      {{ statusMessage }}
    </div>

    <div class="sa-divider" role="presentation"></div>

    <!-- Row 2: Content editor link -->
    <div class="sa-row">
      <div class="sa-row__lede">
        <div class="sa-row__label">Content &amp; images</div>
        <p class="sa-row__help">
          Edit hero copy, upload images, and reorder sections on the v2 template.
        </p>
      </div>
      <div class="sa-row__controls">
        <a :href="templateEditorUrl" class="sa-btn sa-btn--ghost">
          <span class="sa-btn__label">
            Open template editor
            <span class="sa-btn__arrow" aria-hidden="true">→</span>
          </span>
        </a>
      </div>
    </div>
  </section>
</template>

<script>
const DISPLAY_LABELS = {
  'dig-pink': 'Dig Pink (legacy v1)',
  'dig-pink-v2': 'Dig Pink v2 (visual polish)',
};
const VISIBLE_SLUGS = ['dig-pink', 'dig-pink-v2'];

export default {
  name: 'AdminCampaignTypeSelector',

  props: {
    campaign: { type: Object, required: true },
    campaignTypes: { type: Array, required: true },
    updateUrl: { type: String, required: true },
    templateEditorUrl: { type: String, required: true },
    publicUrl: { type: String, required: true },
  },

  data() {
    return {
      selectedTypeId: this.campaign.campaign_type_id || null,
      initialTypeId: this.campaign.campaign_type_id || null,
      saving: false,
      statusMessage: '',
      statusVariant: 'neutral',
    };
  },

  computed: {
    visibleTypes() {
      return this.campaignTypes.filter(t => VISIBLE_SLUGS.includes(t.slug));
    },
    isDirty() {
      return this.selectedTypeId !== this.initialTypeId;
    },
  },

  methods: {
    formatTypeLabel(type) {
      return DISPLAY_LABELS[type.slug] || type.name || type.slug;
    },

    async save() {
      if (!this.isDirty || this.saving) return;
      this.saving = true;
      this.statusMessage = '';
      try {
        const { data } = await axios.put(this.updateUrl, {
          campaign_type_id: this.selectedTypeId,
        });
        this.initialTypeId = data.campaign_type_id ?? null;
        this.selectedTypeId = this.initialTypeId;
        this.statusMessage = 'Template saved. Reload the public page to see it in effect.';
        this.statusVariant = 'success';
      } catch (err) {
        const msg = err?.response?.data?.message || err.message || 'Save failed.';
        this.statusMessage = msg;
        this.statusVariant = 'error';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/* Design tokens — scoped so the panel's aesthetic doesn't leak into the legacy form. */
.sa-panel {
  --sa-ink:       #0f172a;   /* slate-900 */
  --sa-ink-soft:  #334155;   /* slate-700 */
  --sa-muted:     #64748b;   /* slate-500 */
  --sa-line:      #e2e8f0;   /* slate-200 */
  --sa-bg:        #ffffff;
  --sa-bg-alt:    #f8fafc;   /* slate-50 */
  --sa-accent:    #db2777;   /* pink-600 */
  --sa-accent-soft: #fce7f3; /* pink-100 */
  --sa-success:   #047857;   /* emerald-700 */
  --sa-error:     #b91c1c;   /* red-700 */

  position: relative;
  background: var(--sa-bg);
  border: 1px solid var(--sa-line);
  border-radius: 12px;
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 4px 12px rgba(15, 23, 42, 0.04);
  padding: 1.5rem 1.75rem;
  color: var(--sa-ink);
  font-variant-numeric: tabular-nums;
}

/* A thin colored rail on the left edge quietly marks the panel as admin territory */
.sa-panel::before {
  content: "";
  position: absolute;
  inset: 0 auto 0 0;
  width: 3px;
  background: linear-gradient(180deg, var(--sa-ink) 0%, var(--sa-ink) 40%, var(--sa-accent) 100%);
  border-top-left-radius: 12px;
  border-bottom-left-radius: 12px;
}

/* Eyebrow */
.sa-panel__eyebrow {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin-bottom: 1.25rem;
}
.sa-panel__eyebrow-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.22rem 0.625rem 0.22rem 0.5rem;
  background: var(--sa-ink);
  color: #fff;
  border-radius: 999px;
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}
.sa-panel__eyebrow-dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: var(--sa-accent);
  box-shadow: 0 0 0 3px rgba(219, 39, 119, 0.22);
}
.sa-panel__eyebrow-sep {
  color: var(--sa-muted);
  font-size: 0.8rem;
}
.sa-panel__title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--sa-ink-soft);
  letter-spacing: -0.005em;
}

/* Row layout: label/help on left, controls on right. Stacks on narrow screens. */
.sa-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 1.25rem 1.5rem;
}
@media (max-width: 720px) {
  .sa-row {
    grid-template-columns: minmax(0, 1fr);
  }
}

.sa-row__label {
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--sa-ink);
  margin-bottom: 0.2rem;
}
.sa-row__help {
  margin: 0;
  font-size: 0.8rem;
  color: var(--sa-muted);
  line-height: 1.5;
}
.sa-row__controls {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}
@media (max-width: 720px) {
  .sa-row__controls { width: 100%; }
  .sa-select { flex: 1; }
}

/* Inline link inside help copy */
.sa-link {
  color: var(--sa-ink-soft);
  text-decoration: none;
  border-bottom: 1px solid var(--sa-line);
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, monospace;
  font-size: 0.78rem;
  padding-bottom: 1px;
  transition: border-color 0.15s, color 0.15s;
}
.sa-link:hover {
  color: var(--sa-accent);
  border-bottom-color: var(--sa-accent);
}
.sa-link__arrow {
  margin-left: 2px;
  font-size: 0.7rem;
  opacity: 0.7;
}

/* Custom-styled select that doesn't look like a 2008 form control */
.sa-select {
  position: relative;
  min-width: 260px;
}
.sa-select__input {
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  padding: 0.625rem 2.25rem 0.625rem 0.875rem;
  background: var(--sa-bg-alt);
  border: 1px solid var(--sa-line);
  border-radius: 8px;
  font-size: 0.875rem;
  color: var(--sa-ink);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.sa-select__input:hover { border-color: #cbd5e1; }
.sa-select__input:focus {
  outline: none;
  border-color: var(--sa-accent);
  background: var(--sa-bg);
  box-shadow: 0 0 0 3px rgba(219, 39, 119, 0.15);
}
.sa-select__input:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.sa-select__chevron {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--sa-muted);
  font-size: 0.7rem;
  pointer-events: none;
}

/* Buttons — two variants: solid primary (save) and ghost link (open editor) */
.sa-btn {
  appearance: none;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 0.625rem 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: -0.005em;
  cursor: pointer;
  text-decoration: none;
  transition: transform 0.08s, background 0.15s, border-color 0.15s, color 0.15s, box-shadow 0.15s;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
}
.sa-btn__label {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}
.sa-btn:active:not(:disabled) { transform: translateY(1px); }

.sa-btn--primary {
  background: var(--sa-ink);
  color: #fff;
  border-color: var(--sa-ink);
}
.sa-btn--primary:hover:not(:disabled) {
  background: #020617;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.15);
}
.sa-btn--primary:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.2);
}
.sa-btn--primary:disabled {
  background: #e2e8f0;
  color: #94a3b8;
  border-color: #e2e8f0;
  cursor: not-allowed;
}

.sa-btn--ghost {
  background: transparent;
  color: var(--sa-ink);
  border-color: var(--sa-line);
}
.sa-btn--ghost:hover {
  background: var(--sa-bg-alt);
  border-color: #cbd5e1;
  color: var(--sa-ink);
}
.sa-btn--ghost .sa-btn__arrow {
  transition: transform 0.18s;
  display: inline-block;
}
.sa-btn--ghost:hover .sa-btn__arrow {
  transform: translateX(3px);
}

/* Inline spinner in the save button */
.sa-spinner {
  width: 12px;
  height: 12px;
  border-radius: 999px;
  border: 2px solid rgba(255,255,255,0.35);
  border-top-color: #fff;
  animation: sa-spin 0.6s linear infinite;
}
@keyframes sa-spin {
  to { transform: rotate(360deg); }
}

/* Status strip after save */
.sa-status {
  margin-top: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.78rem;
  background: var(--sa-bg-alt);
  color: var(--sa-ink-soft);
  border: 1px solid var(--sa-line);
}
.sa-status--success {
  color: var(--sa-success);
  background: #ecfdf5;
  border-color: #a7f3d0;
}
.sa-status--error {
  color: var(--sa-error);
  background: #fef2f2;
  border-color: #fecaca;
}

/* Hairline divider between rows */
.sa-divider {
  height: 1px;
  background: var(--sa-line);
  margin: 1.25rem 0;
}
</style>
